import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Box,
  Group,
  Menu,
  Skeleton,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconDots, IconPencil, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { milestonesApi } from '../../api/milestones';
import { useAuthStore } from '../../stores/authStore';
import {
  MILESTONE_NOTE_MAX_LENGTH,
  MilestoneNote,
  MilestonePathContext,
} from '../../types/milestones';
import { showApiErrorNotification, showSuccessNotification } from '../../utils/notifications';
import { ActionButton } from '../common/ActionButton';
import { ConfirmationModal } from '../common/ConfirmationModal';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export type MilestoneNotesScope = {
  subawardeeForScope?: string;
  componentProjectId?: string;
};

type MilestoneNotesThreadProps = {
  milestoneId: string;
  path: MilestonePathContext;
  scope?: MilestoneNotesScope;
  /** Known count from the list payload — avoids empty/loading flashes on open. */
  initialNotesCount?: number;
  /** When false, notes are not fetched (e.g. drawer closed). */
  enabled?: boolean;
  onNotesCountChange?: (count: number) => void;
};

function normalizeEmail(value?: string | null): string {
  return (value || '').trim().toLowerCase();
}

function formatNoteTime(value: string): string {
  const local = dayjs.utc(value).tz(dayjs.tz.guess());
  if (!local.isValid()) return '';
  return local.fromNow();
}

/** Prefer local-part for long emails; full address stays in title tooltip. */
function formatAuthorLabel(email: string): string {
  const trimmed = (email || '').trim();
  if (!trimmed) return 'Unknown';
  const at = trimmed.indexOf('@');
  if (at <= 0) return trimmed;
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  // Keep typical corporate emails intact; CSS ellipsis still handles narrow widths.
  if (trimmed.length <= 48) return trimmed;
  if (domain.length <= 18) return trimmed;
  return `${local}@${domain.slice(0, 14)}…`;
}

export function notesCountLabel(count: number): string {
  if (count <= 0) return 'Notes';
  return count === 1 ? '1 note' : `${count} notes`;
}

const compactNoteActionSx = {
  width: 'auto',
  minHeight: 'unset',
  height: 30,
  paddingLeft: 12,
  paddingRight: 12,
  fontSize: 12,
  '@media (max-width: 480px)': {
    width: 'auto',
    minHeight: 'unset',
  },
} as const;

const noteInputStyles = (theme: { colorScheme: string; colors: Record<string, string[]> }) => {
  const focusBorder = theme.colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
  return {
    input: {
      '&:focus, &:focus-within': {
        borderColor: focusBorder,
        boxShadow: 'none',
        outline: 'none',
      },
    },
  };
};


/** Notes thread content for use inside a milestone detail surface. */
export function MilestoneNotesThread({
  milestoneId,
  path,
  scope,
  initialNotesCount = 0,
  enabled = true,
  onNotesCountChange,
}: MilestoneNotesThreadProps) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = useAuthStore((s) => s.hasRole('admin'));
  const currentEmail = normalizeEmail(user?.email);

  const [draft, setDraft] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [notePendingDelete, setNotePendingDelete] = useState<MilestoneNote | null>(null);

  const notesQueryKey = useMemo(
    () => [
      'milestone-notes',
      milestoneId,
      path.objective_id,
      path.key_activity_id,
      path.goal_id,
      path.activity_id,
      scope?.subawardeeForScope ?? null,
      scope?.componentProjectId ?? null,
    ],
    [milestoneId, path, scope]
  );

  const { data, isLoading, error } = useQuery({
    queryKey: notesQueryKey,
    queryFn: () => milestonesApi.getMilestoneNotes(milestoneId, path, scope),
    enabled,
  });


  useEffect(() => {
    if (typeof data?.notes_count === 'number') {
      onNotesCountChange?.(data.notes_count);
    }
  }, [data?.notes_count, onNotesCountChange]);

  useEffect(() => {
    if (error) {
      showApiErrorNotification(error, 'Failed to load milestone notes');
    }
  }, [error]);

  const invalidateMilestoneTrees = () => {
    queryClient.invalidateQueries({ queryKey: ['milestones'] });
    queryClient.invalidateQueries({ queryKey: ['upcomingMilestones'] });
    queryClient.invalidateQueries({ queryKey: ['admin-milestones'] });
  };

  const addMutation = useMutation({
    mutationFn: (body: string) => milestonesApi.addMilestoneNote(milestoneId, path, body, scope),
    onSuccess: async () => {
      setDraft('');
      showSuccessNotification('Note added');
      await queryClient.invalidateQueries({ queryKey: notesQueryKey });
      invalidateMilestoneTrees();
    },
    onError: (err) => showApiErrorNotification(err, 'Failed to add note'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ noteId, body }: { noteId: string; body: string }) =>
      milestonesApi.updateMilestoneNote(milestoneId, noteId, path, body, scope),
    onSuccess: async () => {
      setEditingNoteId(null);
      setEditDraft('');
      showSuccessNotification('Note updated');
      await queryClient.invalidateQueries({ queryKey: notesQueryKey });
      invalidateMilestoneTrees();
    },
    onError: (err) => showApiErrorNotification(err, 'Failed to update note'),
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) =>
      milestonesApi.deleteMilestoneNote(milestoneId, noteId, path, scope),
    onSuccess: async () => {
      setNotePendingDelete(null);
      showSuccessNotification('Note deleted');
      await queryClient.invalidateQueries({ queryKey: notesQueryKey });
      invalidateMilestoneTrees();
    },
    onError: (err) => showApiErrorNotification(err, 'Failed to delete note'),
  });

  const canMutateNote = (note: MilestoneNote): boolean => {
    if (isAdmin) return true;
    return normalizeEmail(note.created_by) === currentEmail;
  };

  const submitNewNote = () => {
    const body = draft.trim();
    if (!body || body.length > MILESTONE_NOTE_MAX_LENGTH || addMutation.isPending) return;
    addMutation.mutate(body);
  };

  const submitEdit = (noteId: string) => {
    const body = editDraft.trim();
    if (!body || body.length > MILESTONE_NOTE_MAX_LENGTH || updateMutation.isPending) return;
    updateMutation.mutate({ noteId, body });
  };

  const notes = data?.notes ?? [];
  const draftLen = draft.trim().length;
  const editLen = editDraft.trim().length;
  // Skeleton while first fetch is in flight; never flash empty state before we know.
  const showSkeleton = enabled && isLoading && notes.length === 0;

  return (
    <>
      <Stack spacing="md">
        <Stack spacing={6}>
          <Textarea
            placeholder="Add a note…"
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            minRows={3}
            maxLength={MILESTONE_NOTE_MAX_LENGTH}
            autosize
            aria-label="New milestone note"
            styles={noteInputStyles}
          />
          <Group position="apart" spacing="xs">
            <Text size="xs" color="dimmed">
              {draftLen}/{MILESTONE_NOTE_MAX_LENGTH}
            </Text>
            <ActionButton
              label="Add note"
              actionType="primary"
              disabled={!draft.trim() || addMutation.isPending}
              isLoading={addMutation.isPending}
              onClick={submitNewNote}
              sx={compactNoteActionSx}
            />
          </Group>
        </Stack>

        {showSkeleton ? (
          <Stack spacing="md" aria-busy="true" aria-label="Loading notes">
            {Array.from({ length: Math.min(Math.max(initialNotesCount, 1), 3) }, (_, index) => (
              <Box key={index}>
                <Skeleton height={14} width={index === 0 ? '92%' : '78%'} mb={8} />
                <Skeleton height={14} width={index === 0 ? '70%' : '88%'} mb={8} />
                <Skeleton height={12} width={140} />
              </Box>
            ))}
          </Stack>
        ) : null}

        <Stack spacing="md">
          {notes.map((note) => {
            const isEditing = editingNoteId === note.id;
            const mutable = canMutateNote(note);

            return (
              <Box
                key={note.id}
                pb="sm"
                sx={(theme) => ({
                  borderBottom: `1px solid ${
                    theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
                  }`,
                  '&:last-of-type': { borderBottom: 'none', paddingBottom: 0 },
                })}
              >
                {isEditing ? (
                  <Stack spacing={6}>
                    <Textarea
                      value={editDraft}
                      onChange={(event) => setEditDraft(event.currentTarget.value)}
                      minRows={3}
                      maxLength={MILESTONE_NOTE_MAX_LENGTH}
                      autosize
                      aria-label="Edit note"
                      styles={noteInputStyles}
                    />
                    <Group position="apart" spacing="xs">
                      <Text size="xs" color="dimmed">
                        {editLen}/{MILESTONE_NOTE_MAX_LENGTH}
                      </Text>
                      <Group spacing={6}>
                        <ActionButton
                          label="Cancel"
                          actionType="secondary"
                          disabled={updateMutation.isPending}
                          onClick={() => {
                            setEditingNoteId(null);
                            setEditDraft('');
                          }}
                          sx={compactNoteActionSx}
                        />
                        <ActionButton
                          label="Save"
                          actionType="primary"
                          disabled={!editDraft.trim() || updateMutation.isPending}
                          isLoading={updateMutation.isPending}
                          onClick={() => submitEdit(note.id)}
                          sx={compactNoteActionSx}
                        />
                      </Group>
                    </Group>
                  </Stack>
                ) : (
                  <Stack spacing={6}>
                    <Text size="sm" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {note.body}
                    </Text>
                    <Group position="apart" spacing="xs" noWrap align="flex-start">
                      <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                        <Text
                          size="xs"
                          color="dimmed"
                          title={note.created_by}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatAuthorLabel(note.created_by)}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {note.created_at ? formatNoteTime(note.created_at) : ''}
                          {note.updated_at ? ' · edited' : ''}
                        </Text>
                      </Stack>
                      {mutable ? (
                        <Menu shadow="md" width={160} position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="gray"
                              aria-label="Note actions"
                              sx={{ flexShrink: 0 }}
                            >
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              icon={<IconPencil size={14} />}
                              onClick={() => {
                                setEditingNoteId(note.id);
                                setEditDraft(note.body);
                              }}
                            >
                              Edit
                            </Menu.Item>
                            <Menu.Item
                              color="red"
                              icon={<IconTrash size={14} />}
                              onClick={() => setNotePendingDelete(note)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      ) : null}
                    </Group>
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </Stack>

      <ConfirmationModal
        opened={Boolean(notePendingDelete)}
        title="Delete note?"
        message="This removes the note from the thread."
        confirmLabel="Delete note"
        confirmAriaLabel="Confirm delete note"
        severity="danger"
        onConfirm={() => {
          if (notePendingDelete) {
            deleteMutation.mutate(notePendingDelete.id);
          }
        }}
        onCancel={() => setNotePendingDelete(null)}
        onClose={() => setNotePendingDelete(null)}
      />
    </>
  );
}
